import { Button, Checkbox, Flex, Icon, Link, Spinner, Text, Tooltip } from '@chakra-ui/react';

import { NavLink as RouterLink } from 'react-router-dom';

import { ColumnDef, createColumnHelper, Row, Table } from '@tanstack/react-table';

import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';

import {
  DocumentDownloadOutline,
  CheckCircleOutline,
  ClockOutline,
  Badge,
  BadgeStatus,
  ExternalLinkOutline,
  EyeOutline,
  PayrollIntegrationSubmissionStatus,
  RefreshOutline,
  WarningOutline,
  ActionIconButton,
} from '@aslanpay/portal-common-lib';

import {
  DeductionsReportActions,
  DeductionsReportBulkActions,
  DeductionsReportRow,
} from 'app/components/UI/Table/Table.types';
import PayCycleSummary from 'app/components/PayCalendars/PayCycleSummary';
import TextWithClipboard from 'app/components/UI/TextWithClipboard';

import DeductionsReportsStatusValues, {
  CompanyDeductionsReportStatus,
} from 'app/constants/deductionsReports';
import { deductionsReportsActionsValues, DeductionsReportsField } from 'app/constants/table';

import {
  hasDeductionsReportProblems,
  isDownloadableDeductionsReportStatus,
  isRetryableDeductionsReport,
} from 'app/utils/deductionsReports';

import {
  getIntegrationSyncHistoryPath,
  getPayCalendarProfilePath,
  getSelectedCompanyPath,
} from 'app/utils/routes';
import { formatCurrency } from 'app/utils/strings';

const formatRelativeSubmissionDate = (submittedAt: string) =>
  formatDistanceToNowStrict(parseISO(submittedAt), { addSuffix: true });

const getSubmissionDetails = (
  submittedAt: string,
  status: PayrollIntegrationSubmissionStatus,
  error?: string,
  submittedRows?: number,
  totalRows?: number
) => {
  const details = [
    `Submitted: ${format(parseISO(submittedAt), 'dd MMM yyyy, HH:mm')}`,
    `Submission: ${status.toLowerCase()}`,
  ];

  if (submittedRows !== undefined && totalRows !== undefined) {
    details.push(`Rows: ${submittedRows} / ${totalRows}`);
  }

  if (error) {
    details.push(`Error: ${error}`);
  }

  return details.join('\n');
};

function getIntegrationSubmissionCell(
  report: DeductionsReportRow,
  actions: DeductionsReportActions
) {
  const { payrollIntegrationSubmittedAt: submittedAt, payrollIntegrationSubmissionStatus: status } =
    report;
  const isResubmitting =
    actions.isResubmitting && actions.resubmittingDeductionsReportId === report.id;
  const resubmitAction = (
    <Tooltip label="Resubmit" placement="top" shouldWrapChildren>
      <ActionIconButton
        aria-label="Resubmit report"
        icon={isResubmitting ? <Spinner size="sm" /> : <RefreshOutline boxSize="18px" />}
        size="xs"
        isDisabled={actions.isResubmitting}
        data-testid={`resubmit-deductions-report-${report.id}`}
        onClick={() => actions.resubmit(report.id)}
      />
    </Tooltip>
  );

  if (!submittedAt || !status || !isValid(parseISO(submittedAt))) {
    return (
      <Flex align="center" gap="6px" minW="120px">
        <ClockOutline aria-label="Integration submission pending" boxSize="16px" role="img" />
        {resubmitAction}
        <Text color="brand.darkGray" whiteSpace="nowrap">
          Pending
        </Text>
      </Flex>
    );
  }

  return (
    <Flex align="center" gap="6px" minW="120px">
      <Tooltip
        hasArrow
        label={getSubmissionDetails(
          submittedAt,
          status,
          report.payrollIntegrationSubmissionError,
          report.payrollIntegrationSubmittedRows,
          report.payrollIntegrationTotalRows
        )}
        placement="top"
        whiteSpace="pre-line"
      >
        <Flex
          align="center"
          aria-label={`Integration submission ${status.toLowerCase()}`}
          color={status === 'SUCCESS' ? 'green.500' : 'red.500'}
          role="img"
          w="fit-content"
        >
          {status === 'SUCCESS' ? (
            <CheckCircleOutline boxSize="16px" />
          ) : (
            <WarningOutline boxSize="16px" />
          )}
        </Flex>
      </Tooltip>
      {resubmitAction}
      <Tooltip label="View sync history" placement="top" shouldWrapChildren>
        <Link
          as={RouterLink}
          to={getIntegrationSyncHistoryPath(report.companyId, 'PAYROLL_OUTPUT')}
          color="brand.darkGray"
          aria-label="View sync history"
          onClick={(event) => event.stopPropagation()}
        >
          <ExternalLinkOutline boxSize="16px" />
        </Link>
      </Tooltip>
      <Text textStyle="extraSmallRegular" color="brand.darkGray" whiteSpace="nowrap">
        {formatRelativeSubmissionDate(submittedAt)}
      </Text>
    </Flex>
  );
}

function getDeductionsReportColumns(
  actions: DeductionsReportActions,
  showIntegrationSubmission: boolean,
  showCompanyColumn = true
) {
  const columnHelper = createColumnHelper<DeductionsReportRow>();

  const fields = Object.keys(DeductionsReportsField).filter(
    (field): field is keyof DeductionsReportRow => field !== 'integration'
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<DeductionsReportRow, any>[] = fields.map((field) => {
    return columnHelper.accessor(field as keyof DeductionsReportRow, {
      cell: (info) => {
        switch (field as keyof typeof DeductionsReportsField) {
          case 'companyName':
            return (
              <Link
                as={RouterLink}
                to={getSelectedCompanyPath(info.row.original.companyId)}
                color="brand.purple"
                textDecoration="underline"
                onClick={(event) => event.stopPropagation()}
              >
                {info.getValue() as string}
              </Link>
            );
          case 'payCalendarName':
            return (
              <Link
                as={RouterLink}
                to={getPayCalendarProfilePath(
                  info.row.original.payCalendarId,
                  info.row.original.companyId
                )}
                color="brand.purple"
                textDecoration="underline"
                onClick={(event) => event.stopPropagation()}
              >
                {info.getValue() as string}
              </Link>
            );
          case 'payCyclePeriod':
            return <PayCycleSummary payCycle={info.row.original.payCycle} />;
          case 'id':
            return info.getValue() && <TextWithClipboard value={info.getValue() as string} />;
          case 'salaryDeductions':
            return formatCurrency(info.getValue() as number, info.row.original.currency);
          case 'createdAt':
            return format(parseISO(info.getValue() as string), 'dd/MM/yyyy HH:mm:ss');
          case 'updatedAt': {
            const updatedAtValue = info.getValue() as string;
            if (!updatedAtValue) return null;
            const parsedDate = parseISO(updatedAtValue);
            return isValid(parsedDate) ? format(parsedDate, 'dd/MM/yyyy HH:mm:ss') : null;
          }
          case 'status': {
            const statusDetails =
              DeductionsReportsStatusValues[info.getValue() as CompanyDeductionsReportStatus];

            return (
              <Badge
                value={statusDetails?.value ?? '-'}
                status={(statusDetails?.color ?? 'created') as BadgeStatus}
              />
            );
          }
          default:
            return info.getValue();
        }
      },
      header: DeductionsReportsField[field as keyof typeof DeductionsReportsField],
      enableSorting: [
        'id',
        'salaryDeductions',
        'totalRecords',
        'createdAt',
        'updatedAt',
        'status',
      ].includes(field),
      meta: {
        getCellContext: ({ row }) => ({
          onClick: () => actions.select(row.original),
        }),
      },
    });
  });

  if (!showCompanyColumn) {
    const companyColumnIndex = columns.findIndex((column) => column.header === 'Company');
    if (companyColumnIndex > -1) {
      columns.splice(companyColumnIndex, 1);
    }
  }

  if (showIntegrationSubmission) {
    columns.push(
      columnHelper.display({
        id: 'integration',
        header: DeductionsReportsField.integration,
        cell: ({ row }) => getIntegrationSubmissionCell(row.original, actions),
        enableSorting: false,
      })
    );
  }

  columns.unshift({
    id: 'select',
    enableSorting: false,
    header: ({ table }: { table: Table<DeductionsReportRow> }) => (
      <Checkbox
        {...{
          isChecked: table.getIsAllPageRowsSelected(),
          isIndeterminate: table.getIsSomePageRowsSelected(),
          onChange: table.getToggleAllPageRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }: { row: Row<DeductionsReportRow> }) => (
      <Checkbox
        {...{
          isChecked: row.getIsSelected(),
          isIndeterminate: row.getIsSomeSelected(),
          onChange: row.getToggleSelectedHandler(),
        }}
      />
    ),
    meta: {
      customStyles: { justifyContent: 'center' },
      pin: 'left',
      withBackground: true,
    },
  });

  columns.push(
    columnHelper.display({
      id: 'options',
      header: 'Actions',
      cell: ({ row }) => {
        const report = row.original;
        const canViewProblems = hasDeductionsReportProblems(report.status);
        const canRetry = isRetryableDeductionsReport(report);
        const canDownload = !canRetry && isDownloadableDeductionsReportStatus(report.status);

        if (!canViewProblems && !canRetry && !canDownload) {
          return undefined;
        }

        return (
          <Flex justify="center" gap="4px">
            {canViewProblems && (
              <Tooltip label="View problems" placement="top" shouldWrapChildren>
                <ActionIconButton
                  aria-label="View problems"
                  icon={<EyeOutline boxSize="18px" />}
                  data-testid={`view-deductions-report-problems-${report.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    actions.viewProblems(report);
                  }}
                />
              </Tooltip>
            )}
            {canRetry && (
              <Tooltip label="Retry report" placement="top" shouldWrapChildren>
                <ActionIconButton
                  aria-label="Retry report"
                  icon={<RefreshOutline boxSize="18px" />}
                  isDisabled={actions.isRetrying}
                  data-testid={`retry-deductions-report-${report.id}`}
                  onClick={() => actions.retry(report.id)}
                />
              </Tooltip>
            )}
            {canDownload && (
              <Tooltip label="Download CSV" placement="top" shouldWrapChildren>
                <ActionIconButton
                  aria-label="Download CSV"
                  icon={<DocumentDownloadOutline boxSize="18px" />}
                  onClick={() => actions.download(report.id)}
                />
              </Tooltip>
            )}
          </Flex>
        );
      },
      meta: {
        pin: 'right',
        withBackground: true,
      },
    })
  );

  return columns;
}

export function getDeductionsReportBulkActionsValues<TData extends object>(
  actions: DeductionsReportBulkActions,
  selectedRows: Row<TData>[]
) {
  const isDownloadDisabled = selectedRows.some(
    (row) => !isDownloadableDeductionsReportStatus((row.original as DeductionsReportRow).status)
  );

  const bulkActions = {
    ...actions,
    download: {
      ...(actions as DeductionsReportBulkActions).download,
      isDisabled: isDownloadDisabled,
    },
  };

  const bulkActionsElements = Object.keys(actions).map((bulkAction) => {
    const actionValue = deductionsReportsActionsValues.find((value) => value.id === bulkAction);

    return (
      <Button
        key={bulkAction}
        size="bulk"
        variant="bulk"
        onClick={() =>
          bulkActions[bulkAction as keyof DeductionsReportBulkActions].action(
            selectedRows.map((row) => (row.original as DeductionsReportRow).id)
          )
        }
        isDisabled={bulkActions[bulkAction as keyof DeductionsReportBulkActions].isDisabled}
      >
        <Icon as={actionValue?.icon} boxSize="20px" />
        <Text>{actionValue?.value}</Text>
      </Button>
    );
  });

  return { label: 'report', bulkActions: bulkActionsElements };
}

export default getDeductionsReportColumns;
